import { Alert, Box, TextField } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { addLinkedinMessageToQueue } from "../../../api/messaging/linkedin-message";
import { EmojiPickerButton } from "../../shared/EmojiPickerButton";
import type { ActivityPanelHandle, ActivityPanelProps } from "./panel-types";

interface LinkedInPanelProps extends ActivityPanelProps {
	personId: number;
	userId: number;
	isFirstDegree: boolean;
}

export const LinkedInPanel = forwardRef<ActivityPanelHandle, LinkedInPanelProps>(
	({ personId, userId, isFirstDegree, onCanSaveChange }, ref) => {
		const [content, setContent] = useState("");
		const cursorRef = useRef({ start: 0, end: 0 });
		const inputRef = useRef<HTMLTextAreaElement>(null);

		const canSave = isFirstDegree && content.trim().length > 0;

		useEffect(() => {
			onCanSaveChange(canSave);
		}, [canSave, onCanSaveChange]);

		useImperativeHandle(ref, () => ({
			save: async () => {
				await addLinkedinMessageToQueue({
					from: userId,
					to: personId,
					text: content,
					sendImmediately: true,
				});
			},
		}));

		const handleEmojiSelect = (emoji: string) => {
			const start = cursorRef.current.start;
			const end = cursorRef.current.end;
			const newContent =
				content.substring(0, start) + emoji + content.substring(end);
			setContent(newContent);

			const newPos = start + emoji.length;
			cursorRef.current = { start: newPos, end: newPos };

			setTimeout(() => {
				if (inputRef.current) {
					inputRef.current.focus();
					inputRef.current.setSelectionRange(newPos, newPos);
				}
			}, 0);
		};

		const handleSelect = () => {
			if (inputRef.current) {
				cursorRef.current = {
					start: inputRef.current.selectionStart ?? 0,
					end: inputRef.current.selectionEnd ?? 0,
				};
			}
		};

		if (!isFirstDegree) {
			return (
				<Alert severity="warning" data-component="not-connected-alert">
					Cannot send LinkedIn message - not connected with this person. Send
					a connection request first.
				</Alert>
			);
		}

		return (
			<Box data-component="linkedin-content-wrapper">
				<TextField
					label="Message"
					value={content}
					onChange={(e) => setContent(e.target.value)}
					onSelect={handleSelect}
					inputRef={inputRef}
					multiline
					minRows={8}
					fullWidth
					placeholder="Write your LinkedIn message..."
					data-component="linkedin-content-input"
				/>
				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						mt: 0.5,
					}}
				>
					<EmojiPickerButton onEmojiSelect={handleEmojiSelect} />
				</Box>
			</Box>
		);
	}
);

LinkedInPanel.displayName = "LinkedInPanel";
